import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, Check, Loader2, ShieldCheck } from 'lucide-react';
import { ChainChoice, ChainBillSummary } from '../components/ChainSignup';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api, { isDesktop } from '../lib/api';
import { setDesktopToken } from '../lib/desktopToken';
import { useAuth } from '../contexts/AuthContext';

const STEPS = ['Pharmacy', 'Plan', 'Owner'];
const empty = {
  pharmacy_name: '', town: '', phone: '', license_number: '',
  plan: 'essentials', cycle: 'monthly', owner_name: '', email: '',
  password: '', confirm_password: '', accepted_terms: false, website: '', referral_code: '', planned_branches: 1,
};

const errorText = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(item => item?.msg || 'Invalid field').join(' · ');
  return 'We could not create your pharmacy. Please review the form.';
};

export default function PharmacySignupPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, setAuthUser } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [currency, setCurrency] = useState(() => params.get('currency') === 'ghs' ? 'ghs' : 'usd');
  const [referrerName, setReferrerName] = useState('');
  const [form, setForm] = useState(() => {
    const rawPlan = (params.get('plan') || '').toLowerCase().replace(/\s+/g, '-');
    const plan = ['essentials', 'professional'].includes(rawPlan) ? rawPlan : 'essentials';
    const cycle = params.get('cycle') === 'annual' ? 'annual' : 'monthly';
    const referral_code = (params.get('ref') || '').trim().toUpperCase();
    return { ...empty, plan, cycle, referral_code };
  });
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));

  useEffect(() => {
    if (!form.referral_code) return;
    api.get(`/public/referrals/${encodeURIComponent(form.referral_code)}`)
      .then((res) => { if (res.data?.valid) setReferrerName(res.data.pharmacy_name || ''); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Fetch live USD prices (for the $ toggle) + actual GHS prices Paystack will charge */
  const [planPrices, setPlanPrices] = useState({ essentials: 25, professional: 89 });
  const [ghsPlans, setGhsPlans] = useState({ essentials: { monthly: 350, annual: 3360, branches: 1 }, professional: { monthly: 1200, annual: 11520, branches: 5 } });
  const [slotPrice, setSlotPrice] = useState(150);
  useEffect(() => {
    api.get('/public/plans').then((res) => {
      const plans = res.data?.plans;
      if (Array.isArray(plans)) {
        const map = {};
        for (const p of plans) {
          const key = (p.name || '').toLowerCase().replace(/\s+/g, '-');
          if (key) map[key] = { price: p.price, discount: p.annual_discount_pct ?? 20 };
        }
        if (map.essentials) setPlanPrices((prev) => ({ ...prev, essentials: map.essentials.price }));
        if (map.professional) setPlanPrices((prev) => ({ ...prev, professional: map.professional.price }));
      }
    }).catch(() => {});
    api.get('/billing/plans').then((res) => {
      const list = res.data?.plans;
      if (Array.isArray(list)) {
        const map = {};
        for (const p of list) if (p.key) map[p.key] = { monthly: p.monthly_ghs, annual: p.annual_ghs, branches: p.limits?.branches ?? 1 };
        setGhsPlans((prev) => ({ ...prev, ...map }));
      }
      if (res.data?.branch_slot?.price_ghs) {
        setSlotPrice(res.data.branch_slot.price_ghs);
      }
    }).catch(() => {});
  }, []);

  /* GHS: the exact figure Paystack will charge. USD: live price × rate, informational only. */
  const prices = useMemo(() => {
    const essUsd = planPrices.essentials;
    const proUsd = planPrices.professional;
    const annualDiscount = 0.8; // 20% off
    if (currency === 'ghs') {
      const cyclePick = (plan) => form.cycle === 'annual' ? Math.round(ghsPlans[plan].annual / 12) : ghsPlans[plan].monthly;
      return { essentials: cyclePick('essentials'), professional: cyclePick('professional'), symbol: 'GHS' };
    }
    return {
      essentials: form.cycle === 'annual' ? Math.round(essUsd * 12 * annualDiscount / 12) : essUsd,
      professional: form.cycle === 'annual' ? Math.round(proUsd * 12 * annualDiscount / 12) : proUsd,
      symbol: '$',
    };
  }, [form.cycle, currency, planPrices, ghsPlans]);

  // Already signed in → leave the signup page. Skipped right after a signup so
  // the server's next_step (/standing or /branches?setup=chain) wins the race.
  const justSignedUp = useRef(false);
  useEffect(() => {
    if (user && !justSignedUp.current) {
      navigate(user.role === 'platform_owner' ? '/platform' : '/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const next = () => {
    if (step === 0 && (!form.pharmacy_name.trim() || !form.town.trim() || !form.phone.trim())) {
      toast.error('Add the pharmacy name, town and contact phone'); return;
    }
    setStep(value => Math.min(2, value + 1));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (form.password !== form.confirm_password) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.confirm_password;
      const { data } = await api.post('/public/pharmacy-signup', payload);
      // Desktop Terminal (cross-origin to the cloud API) has no cookie jar to
      // rely on — it needs the Bearer token the backend sends in the body,
      // same as the regular sign-in flow (see AuthContext.js::completeLogin).
      if (isDesktop()) await setDesktopToken(data.token);
      justSignedUp.current = true;
      setAuthUser(data.user);
      toast.success('Your pharmacy workspace is ready');
      navigate(data.next_step || '/standing', { replace: true });
    } catch (error) {
      toast.error(errorText(error?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#08120f] text-slate-100 lg:grid lg:grid-cols-[0.85fr_1.15fr]" data-testid="pharmacy-signup-page">
      <aside className="relative hidden overflow-hidden lg:block" data-testid="pharmacy-signup-visual">
        <img src="/images/welcome/hero.webp" alt="Pharmacist supporting a patient" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08120f] via-[#08120f]/65 to-[#08120f]/20" />
        <div className="absolute inset-x-10 bottom-12 max-w-lg">
          <p className="text-sm font-semibold text-emerald-300">{form.referral_code ? '21-day trial · no card required' : '14-day trial · no card required'}</p>
          <h1 className="mt-3 text-4xl font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Open your pharmacy workspace today.</h1>
          <p className="mt-4 text-base leading-relaxed text-slate-300">Start with secure operations, then complete business KYC before going live.</p>
        </div>
      </aside>

      <main className="flex min-h-screen items-center justify-center p-5 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-2xl" data-testid="pharmacy-signup-form">
          <div className="mb-8 flex items-center justify-between gap-4">
            <Link to="/welcome" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="pharmacy-signup-back-link"><ArrowLeft size={16} /> DutiLyne</Link>
            <Link to="/login" className="text-sm text-emerald-300 hover:text-emerald-200" data-testid="pharmacy-signup-login-link">Already registered? Sign in</Link>
          </div>

          <div className="mb-8 grid grid-cols-3 gap-2" data-testid="pharmacy-signup-progress">
            {STEPS.map((label, index) => <div key={label} data-testid={`pharmacy-signup-step-${index}`} className={`border-t-2 pt-2 text-xs font-semibold ${index <= step ? 'border-emerald-400 text-emerald-300' : 'border-white/10 text-slate-500'}`}>{index + 1}. {label}</div>)}
          </div>

          <div className="border border-white/10 bg-white/[0.04] p-5 sm:p-8">
            {step === 0 && <section className="space-y-5" data-testid="pharmacy-signup-pharmacy-step">
              <div><p className="text-xs font-semibold uppercase text-emerald-300">Your business</p><h2 className="mt-2 text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Tell us about the pharmacy</h2></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><Label htmlFor="signup-pharmacy-name">Pharmacy name</Label><Input id="signup-pharmacy-name" value={form.pharmacy_name} onChange={e => set('pharmacy_name', e.target.value)} data-testid="pharmacy-signup-name-input" /></div>
                <div><Label htmlFor="signup-town">Town</Label><Input id="signup-town" value={form.town} onChange={e => set('town', e.target.value)} data-testid="pharmacy-signup-town-input" /></div>
                <div><Label htmlFor="signup-phone">Contact phone</Label><Input id="signup-phone" value={form.phone} onChange={e => set('phone', e.target.value)} data-testid="pharmacy-signup-phone-input" /></div>
                <div className="sm:col-span-2"><Label htmlFor="signup-license">Pharmacy licence number <span className="text-slate-500">(can be completed during KYC)</span></Label><Input id="signup-license" value={form.license_number} onChange={e => set('license_number', e.target.value)} data-testid="pharmacy-signup-license-input" /></div>
                <div className="sm:col-span-2"><ChainChoice value={form.planned_branches} onChange={(n) => setForm(f => ({ ...f, planned_branches: n, plan: n > 1 && f.planned_branches === 1 ? 'professional' : f.plan }))} /></div>
                <div className="hidden" aria-hidden="true"><Label htmlFor="signup-website">Website</Label><Input id="signup-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={e => set('website', e.target.value)} data-testid="pharmacy-signup-website-input" /></div>
              </div>
            </section>}

            {step === 1 && <section className="space-y-5" data-testid="pharmacy-signup-plan-step">
              <div><p className="text-xs font-semibold uppercase text-emerald-300">Start on trial</p><h2 className="mt-2 text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Choose your working plan</h2></div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex border border-white/10 p-1" data-testid="pharmacy-signup-cycle-control">
                  {['monthly', 'annual'].map(cycle => <button type="button" key={cycle} onClick={() => set('cycle', cycle)} className={`min-h-11 px-5 text-sm font-semibold ${form.cycle === cycle ? 'bg-emerald-400 text-emerald-950' : 'text-slate-300'}`} data-testid={`pharmacy-signup-cycle-${cycle}`}>{cycle === 'annual' ? 'Annual · save 20%' : 'Monthly'}</button>)}
                </div>
                <div className="inline-flex border border-white/10 p-0.5 rounded-full" data-testid="signup-currency-toggle">
                  {['usd', 'ghs'].map((c) => (
                    <button key={c} type="button" onClick={() => setCurrency(c)}
                      className={`px-3 py-1 text-xs font-bold uppercase tracking-wide rounded-full transition-colors ${currency === c ? 'bg-white/10 text-white' : 'text-slate-500 hover:text-slate-300'}`}
                      data-testid={`signup-currency-${c}`}
                    >{c}</button>
                  ))}
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[['essentials', 'Essentials', 'Single-location essentials'], ['professional', 'Professional', 'Growing teams and branches']].map(([key, name, note]) => <button type="button" key={key} onClick={() => set('plan', key)} className={`min-h-36 border p-5 text-left transition-transform hover:-translate-y-0.5 ${form.plan === key ? 'border-emerald-400 bg-emerald-400/10' : 'border-white/10 bg-black/10'}`} data-testid={`pharmacy-signup-plan-${key}`}><Building2 className="mb-4 text-emerald-300" size={20} /><strong className="block text-lg">{name}</strong><span className="mt-1 block text-sm text-slate-400">{note}</span><span className="mt-4 block text-sm font-semibold text-emerald-300">{prices.symbol === '$' ? `$${prices[key].toLocaleString()}` : `GHS ${prices[key].toLocaleString()}`} / month</span></button>)}
              </div>
              {form.planned_branches > 1 && <ChainBillSummary locations={form.planned_branches} plan={ghsPlans[form.plan]} planName={form.plan === 'professional' ? 'Professional' : 'Essentials'} slotPrice={slotPrice} cycle={form.cycle} />}
              <p className="flex items-center gap-2 text-sm text-slate-400"><Check size={15} className="text-emerald-300" /> You can change plans before the trial ends.</p>
            </section>}

            {step === 2 && <section className="space-y-5" data-testid="pharmacy-signup-owner-step">
              <div><p className="text-xs font-semibold uppercase text-emerald-300">Workspace owner</p><h2 className="mt-2 text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Create the super admin</h2></div>
              {referrerName && <div className="flex items-center gap-2 border-l-2 border-emerald-400 bg-emerald-400/10 p-3 text-sm text-emerald-200" data-testid="pharmacy-signup-referrer-banner"><Check size={16} className="text-emerald-300 shrink-0" /> Referred by <b className="mx-1">{referrerName}</b> — you get 7 extra trial days now, and they get 30 extra days once you're a paying customer.</div>}
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label htmlFor="signup-owner-name">Full name</Label><Input id="signup-owner-name" value={form.owner_name} onChange={e => set('owner_name', e.target.value)} data-testid="pharmacy-signup-owner-name-input" /></div>
                <div><Label htmlFor="signup-owner-email">Work email</Label><Input id="signup-owner-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} data-testid="pharmacy-signup-email-input" /></div>
                <div><Label htmlFor="signup-password">Password</Label><Input id="signup-password" type="password" value={form.password} onChange={e => set('password', e.target.value)} data-testid="pharmacy-signup-password-input" /></div>
                <div><Label htmlFor="signup-confirm-password">Confirm password</Label><Input id="signup-confirm-password" type="password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} data-testid="pharmacy-signup-confirm-password-input" /></div>
                <div className="sm:col-span-2"><Label htmlFor="signup-referral">Referral code <span className="text-slate-500">(optional)</span></Label><Input id="signup-referral" value={form.referral_code} onChange={e => set('referral_code', e.target.value.toUpperCase())} maxLength={24} placeholder="e.g. ACCRA7F3K" data-testid="pharmacy-signup-referral-input" /></div>
              </div>
              <p className="text-xs leading-relaxed text-slate-400" data-testid="pharmacy-signup-password-policy">Use at least 12 characters with upper and lower case, a number and a symbol.</p>
              <label className="flex items-start gap-3 text-sm text-slate-300"><input type="checkbox" className="mt-1 h-4 w-4" checked={form.accepted_terms} onChange={e => set('accepted_terms', e.target.checked)} data-testid="pharmacy-signup-terms-checkbox" /><span>I confirm I am authorised to create this pharmacy workspace and accept the service terms and privacy notice.</span></label>
              <div className="flex items-center gap-2 border-l-2 border-emerald-400 bg-emerald-400/5 p-3 text-xs text-slate-300" data-testid="pharmacy-signup-security-note"><ShieldCheck size={17} className="text-emerald-300" /> Business KYC is required before the pharmacy can move from trial to active.</div>
            </section>}

            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
              <Button type="button" variant="ghost" onClick={() => setStep(value => Math.max(0, value - 1))} disabled={step === 0 || busy} data-testid="pharmacy-signup-previous-button"><ArrowLeft size={16} className="mr-2" /> Back</Button>
              {step < 2 ? <Button type="button" onClick={next} className="bg-emerald-400 text-emerald-950 hover:bg-emerald-300" data-testid="pharmacy-signup-next-button">Continue <ArrowRight size={16} className="ml-2" /></Button> : <Button type="submit" disabled={busy || !form.accepted_terms} className="bg-emerald-400 text-emerald-950 hover:bg-emerald-300" data-testid="pharmacy-signup-submit-button">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ShieldCheck size={16} className="mr-2" />} Create pharmacy workspace</Button>}
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}